import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { faMemo, faUserCheck, faUserPlus, faUserSlash } from "@fortawesome/pro-regular-svg-icons";
import {
	faArrowDownToLine,
	faBan,
	faChartArea,
	faCircleCheck,
	faClone,
	faGear,
	faMemoCircleCheck,
	faPen,
	faPencil,
	faRotate,
	faTrash,
	faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";

import {
	ButtonLoading,
	ButtonStandard,
	DropDownMenu,
	DropDownMenuOption,
	Icon,
	Text,
	toast,
} from "@performyard/py-components";

import type { EmployeeQuery } from "../../../../components/EmployeeQueryBuilder/types";
import Modal from "../../../../components/Modal";
import DownloadCsvModal, { CsvDownloadType } from "./DownloadCsvModal";
import { EditAutomationRulesModal } from "./EditAutomationRulesModal";

import {
	useBulkApproveEasyAddFormsForReviewCycle,
	useBulkDeactivateEasyAddFormsForReviewCycle,
	useCloseReviewCycle,
	useDeleteReviewCycle,
	useDownloadGoalDiscussionCsv,
	useExportReviewCycleReviewReportsDetailsCsv,
	usePatchAutoflow,
	useRbacObjectPermissions,
	useReviewCycleDetails,
	useReviewCycleReviewsDetailsStream,
} from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import { useReviewCycleBuilderPageContext } from "../hooks/ReviewCycleBuilderPageContext";

// Import job selectors for tracking download progress
import { getIsJobCompleted, getJobFileDownload } from "../../../../selectors/jobs";

import pubSub from "../../../../utils/PubSub";

import { EditCycleDetailsModal } from "../EditCycleDetailsModal";

type DetailsOptionsDropdownProps = {
	reviewCycleId: string;
};

type Modal = {
	title: string;
	description: string;
	confirmButtonText: string;
	action: () => void;
};

const DetailsOptionsDropdown = ({ reviewCycleId }: DetailsOptionsDropdownProps) => {
	const navigate = useNavigate();
	const { fullRefresh } = useReviewCycleBuilderPageContext();
	const { data: reviewCycleReviewsDetails } = useReviewCycleReviewsDetailsStream(reviewCycleId);

	const { value: automatedReviewCyclesEnabled } = useFeatureFlagQuery("autoflow", false);
	const { value: isOptionsRefreshEnabled } = useFeatureFlagQuery(
		"ff-temp-is-review-cycle-options-refresh-enabled",
		false
	);
	const [csvJobID, setCsvJobID] = useState<string | null>(null);
	const [modal, setModal] = useState<Modal | null>(null);
	const [isEditAutomationRulesOpen, setIsEditAutomationRulesOpen] = useState(false);
	const [isDownloadCsvModalOpen, setIsDownloadCsvModalOpen] = useState(false);
	const [editCycleDetailsOpen, setEditCycleDetailsOpen] = useState(false);

	const { data: reviewCycleDetails } = useReviewCycleDetails(reviewCycleId);

	const autoflowUserQuery = useMemo<EmployeeQuery | null>(() => {
		const queryTask = reviewCycleDetails?.autoflow?.tasks.find((task) => task.taskType === "query:user");
		return queryTask?.taskType === "query:user" ? queryTask.config.query : null;
	}, [reviewCycleDetails]);

	// Job tracking for auto-download
	const downloadUrl = useSelector((state) => (csvJobID ? getJobFileDownload(state, csvJobID) : null));
	const isCompleted = useSelector((state) => (csvJobID ? getIsJobCompleted(state, csvJobID) : null));
	const validDownloadUrl = useRef<string | null>(null);

	const { data: permissions = [] } = useRbacObjectPermissions("review", reviewCycleId);

	// Calculate if there are any forms awaiting approval
	const hasFormsAwaitingApproval = useMemo(() => {
		if (!reviewCycleReviewsDetails?.length) return false;
		return reviewCycleReviewsDetails.some((review) => review.pendingThreeSixtyFormsCount > 0);
	}, [reviewCycleReviewsDetails]);

	const { mutate: downloadGoalDiscussionCsv } = useDownloadGoalDiscussionCsv({
		onSuccess: (response) => {
			toast.success("CSV download started successfully");
			if (response?.jobID) {
				setCsvJobID(response.jobID);
			} else {
				console.warn("No jobID received in response:", response);
			}
		},
		onError: () => {
			toast.error("Failed to create CSV download job");
		},
	});

	const { mutate: exportReviewDataCsv } = useExportReviewCycleReviewReportsDetailsCsv({
		onSuccess: (response) => {
			toast.success("CSV download started successfully");
			if (response?.jobId) {
				setCsvJobID(response.jobId);
			} else {
				console.warn("No jobId received in response:", response);
			}
		},
		onError: () => {
			toast.error("Failed to create CSV download job");
		},
	});

	const handleDownloadGoalDiscussionCsv = () => {
		// Publish async frequency check like DownloadButton does
		pubSub.publish("check async frequency", { frequency: 500 });
		downloadGoalDiscussionCsv({ reviewCycleId });
	};

	const handleDownloadReviewDataCsv = () => {
		pubSub.publish("check async frequency", { frequency: 500 });
		exportReviewDataCsv({
			reviewCycleId,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
	};

	const handleDownloadCsvSelection = (csvType: CsvDownloadType) => {
		setIsDownloadCsvModalOpen(false);
		if (csvType === "goal-discussion") {
			handleDownloadGoalDiscussionCsv();
		} else {
			handleDownloadReviewDataCsv();
		}
	};

	const handleDownloadComplete = () => {
		setCsvJobID(null);
	};

	// Auto-download effect - following the DownloadButton pattern
	useEffect(() => {
		if (downloadUrl) {
			validDownloadUrl.current = downloadUrl;
		}
	}, [downloadUrl]);

	useEffect(() => {
		// Match DownloadButton logic exactly: autoDownload && isCompleted && validDownloadUrl.current
		if (csvJobID && isCompleted && validDownloadUrl.current) {
			window.open(validDownloadUrl.current, "_blank");
			handleDownloadComplete();
		}
	}, [csvJobID, downloadUrl, isCompleted]);

	const { mutate: closeReviewCycle, isPending: isClosing } = useCloseReviewCycle({
		onSuccess: () => {
			toast.success("Review cycle closed successfully");
			fullRefresh();
			setModal(null);
		},
	});

	const { mutate: bulkDeactivateEasyAddFormsForReviewCycle } = useBulkDeactivateEasyAddFormsForReviewCycle({
		onSuccess: () => {
			toast.success("360 forms deactivated successfully");
			fullRefresh();
			setModal(null);
		},
	});

	const { mutate: bulkApproveEasyAddFormsForReviewCycle } = useBulkApproveEasyAddFormsForReviewCycle({
		onSuccess: () => {
			toast.success("360 forms approved successfully");
			fullRefresh();
			setModal(null);
		},
	});

	const { mutate: deleteReviewCycle } = useDeleteReviewCycle({
		onSuccess: () => {
			toast.success("Review cycle deleted successfully");
			navigate("/company/reviews/cycles");
			setModal(null);
		},
	});

	const { mutate: patchAutoflow } = usePatchAutoflow({
		onSuccess: () => {
			toast.success("Automation settings updated successfully");
			fullRefresh();
			setModal(null);
		},
	});

	const legacyOptions = useMemo<DropDownMenuOption[]>(() => {
		const options: DropDownMenuOption[] = [];

		if (permissions?.includes("review_cycle:visualizeData")) {
			options.push({
				key: "visualize",
				label: "Visualize Data",
				right: <Icon icon={faChartArea} size={16} color="neutral-7" />,
				href: `/review-cycle-data/${reviewCycleId}/`,
			});
		}

		if (permissions?.includes("review_cycle:editCycle")) {
			options.push({
				key: "edit",
				label: "Edit",
				right: <Icon icon={faPen} size={16} color="neutral-7" />,
				href: `/review-cycle/${reviewCycleId}/edit/`,
			});
		}

		if (automatedReviewCyclesEnabled && reviewCycleDetails?.autoflow) {
			options.push({
				key: "edit-automation-rules",
				label: "Edit Automation Rules",
				right: <Icon icon={faRotate} size={16} color="neutral-7" />,
				href: `/review-cycle/${reviewCycleId}/edit-automation/`,
			});

			if (reviewCycleDetails?.autoflow?.enabled) {
				options.push({
					key: "disable-automation",
					label: "Disable Automation",
					right: <Icon icon={faUserSlash} size={16} color="neutral-7" />,
					onClick: () => {
						setModal({
							title: "Disable Automation?",
							description:
								"The cycle will remain open, but Subjects will no longer be automatically added.",
							confirmButtonText: "Disable Automation",
							action: () =>
								patchAutoflow({
									id: reviewCycleDetails?.autoflow?._id?.toString() || "",
									data: { enabled: false },
								}),
						});
					},
				});
			} else {
				options.push({
					key: "enable-automation",
					label: "Enable Automation",
					right: <Icon icon={faUserCheck} size={16} color="neutral-7" />,
					onClick: () => {
						patchAutoflow({
							id: reviewCycleDetails?.autoflow?._id?.toString() || "",
							data: { enabled: true },
						});
					},
				});
			}
		}

		if (permissions?.includes("review_cycle:addSubjects")) {
			options.push({
				key: "add-subjects",
				label: "Add Subjects",
				right: <Icon icon={faUserPlus} size={16} color="neutral-7" />,
				href: `/review-cycle/${reviewCycleId}/add/`,
			});
		}

		if (automatedReviewCyclesEnabled && permissions?.includes("review_cycle:viewCycleAssignmentLog")) {
			options.push({
				key: "view-assignment-log",
				label: "View Assignment Log",
				right: <Icon icon={faMemo} size={16} color="neutral-7" />,
				href: `/review-cycle/${reviewCycleId}/assignment-log/`,
			});
		}

		if (permissions?.includes("review_cycle:editCycle") && reviewCycleDetails?.hasEasyAdds) {
			options.push({
				key: "deactivate-360-forms",
				label: "Deactivate 360 Forms",
				right: <Icon icon={faBan} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Deactivate 360 Forms",
						description: "Are you sure you want to deactivate 360 forms?",
						confirmButtonText: "Deactivate 360 Forms",
						action: () => bulkDeactivateEasyAddFormsForReviewCycle({ reviewCycleId }),
					});
				},
			});
		}

		if (
			permissions?.includes("review_cycle:editCycle") &&
			reviewCycleDetails?.hasEasyAdds &&
			hasFormsAwaitingApproval
		) {
			options.push({
				key: "bulk-approve-360-forms",
				label: "Approve 360 Forms",
				right: <Icon icon={faMemoCircleCheck} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Approve 360 Forms",
						description: "Are you sure you want to approve all 360 forms?",
						confirmButtonText: "Approve 360 Forms",
						action: () => bulkApproveEasyAddFormsForReviewCycle({ reviewCycleId }),
					});
				},
			});
		}

		if (permissions?.includes("review_cycle:createCycle")) {
			options.push({
				key: "copy",
				label: "Copy",
				right: <Icon icon={faClone} size={16} color="neutral-7" />,
				href: `/review-cycle/${reviewCycleId}/copy/`,
			});
		}

		if (permissions?.includes("review_cycle:downloadCycleCsv") && reviewCycleDetails?.hasGoalDiscussion) {
			options.push({
				key: "download-goal-discussion",
				label: "Download Goal Discussion CSV",
				right: <Icon icon={faArrowDownToLine} size={16} color="neutral-7" />,
				onClick: () => {
					handleDownloadGoalDiscussionCsv();
				},
			});
		}

		if (permissions?.includes("review_cycle:closeCycle") && reviewCycleDetails?.status !== "Completed") {
			options.push({
				key: "close-review-cycle",
				label: "Close Cycle",
				right: <Icon icon={faCircleCheck} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Close Cycle",
						description: "Are you sure you want to close this review cycle?",
						confirmButtonText: "Close Cycle",
						action: () => closeReviewCycle({ reviewCycleId }),
					});
				},
			});
		}

		const canDelete =
			(reviewCycleDetails?.startDate && reviewCycleDetails?.startDate > new Date()) ||
			reviewCycleDetails?.reviewCount === 0;
		if (permissions?.includes("review_cycle:deleteCycle") && canDelete) {
			options.push({
				key: "delete-review-cycle",
				label: "Delete Cycle",
				right: <Icon icon={faTrash} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Delete Cycle",
						description: "Are you sure you want to delete this review cycle?",
						confirmButtonText: "Delete Cycle",
						action: () => deleteReviewCycle({ reviewCycleId }),
					});
				},
			});
		}
		return options;
	}, [reviewCycleDetails, permissions, hasFormsAwaitingApproval]);

	const refreshedOptions = useMemo<DropDownMenuOption[]>(() => {
		const options: DropDownMenuOption[] = [];
		const isAutomated = automatedReviewCyclesEnabled && !!reviewCycleDetails?.autoflow;

		if (permissions?.includes("review_cycle:editCycle")) {
			options.push({
				key: "edit-cycle-details",
				label: "Edit Cycle Details",
				right: <Icon icon={faPencil} size={16} color="neutral-7" />,
				onClick: () => setEditCycleDetailsOpen(true),
			});
		}

		if (permissions?.includes("review_cycle:addFormsInCycle")) {
			options.push({
				key: "add-forms",
				label: "Add Forms",
				right: <Icon icon={faMemo} size={16} color="neutral-7" />,
				onClick: () => navigate(`/review-cycle/${reviewCycleId}/add-forms/`),
			});
		}

		if (permissions?.includes("review_cycle:createCycle")) {
			options.push({
				key: "copy",
				label: "Copy",
				right: <Icon icon={faClone} size={16} color="neutral-7" />,
				onClick: () => navigate(`/review-cycle/create?copyCycleId=${reviewCycleId}`),
			});
		}

		if (permissions?.includes("review_cycle:visualizeData")) {
			options.push({
				key: "visualize",
				label: "Visualize Data",
				right: <Icon icon={faChartArea} size={16} color="neutral-7" />,
				href: `/review-cycle-data/${reviewCycleId}/`,
			});
		}

		if (isAutomated) {
			options.push({
				key: "edit-automation-rules",
				label: "Edit Automation Rules",
				right: <Icon icon={faRotate} size={16} color="neutral-7" />,
				onClick: () => setIsEditAutomationRulesOpen(true),
			});

			if (reviewCycleDetails?.autoflow?.enabled) {
				options.push({
					key: "disable-automation",
					label: "Disable Automation",
					right: <Icon icon={faUserSlash} size={16} color="neutral-7" />,
					onClick: () => {
						setModal({
							title: "Disable Automation?",
							description:
								"The cycle will remain open, but Subjects will no longer be automatically added.",
							confirmButtonText: "Disable Automation",
							action: () =>
								patchAutoflow({
									id: reviewCycleDetails?.autoflow?._id?.toString() || "",
									data: { enabled: false },
								}),
						});
					},
				});
			} else {
				options.push({
					key: "enable-automation",
					label: "Enable Automation",
					right: <Icon icon={faUserCheck} size={16} color="neutral-7" />,
					onClick: () => {
						patchAutoflow({
							id: reviewCycleDetails?.autoflow?._id?.toString() || "",
							data: { enabled: true },
						});
					},
				});
			}
		}

		if (isAutomated && permissions?.includes("review_cycle:viewCycleAssignmentLog")) {
			options.push({
				key: "view-assignment-log",
				label: "View Assignment Log",
				right: <Icon icon={faMemo} size={16} color="neutral-7" />,
				href: `/review-cycle/${reviewCycleId}/assignment-log/`,
			});
		}

		if (permissions?.includes("review_cycle:editCycle") && reviewCycleDetails?.hasEasyAdds) {
			options.push({
				key: "edit-360-form-settings",
				label: "Edit 360 Form Settings",
				right: <Icon icon={faPencil} size={16} color="neutral-7" />,
				onClick: () => navigate(`/review-cycle/${reviewCycleId}/edit-360-form-settings/`),
			});
		}

		if (
			permissions?.includes("review_cycle:editCycle") &&
			reviewCycleDetails?.hasEasyAdds &&
			hasFormsAwaitingApproval
		) {
			options.push({
				key: "bulk-approve-360-forms",
				label: "Approve 360 Forms",
				right: <Icon icon={faMemoCircleCheck} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Approve 360 Forms",
						description: "Are you sure you want to approve all 360 forms?",
						confirmButtonText: "Approve 360 Forms",
						action: () => bulkApproveEasyAddFormsForReviewCycle({ reviewCycleId }),
					});
				},
			});
		}

		if (permissions?.includes("review_cycle:downloadCycleCsv")) {
			options.push({
				key: "download-csv",
				label: "Download CSV",
				right: <Icon icon={faArrowDownToLine} size={16} color="neutral-7" />,
				onClick: () => {
					// Without a goal discussion CSV to choose from, skip the modal
					// and serve the review data CSV directly
					if (reviewCycleDetails?.hasGoalDiscussion) {
						setIsDownloadCsvModalOpen(true);
					} else {
						handleDownloadReviewDataCsv();
					}
				},
			});
		}

		if (permissions?.includes("review_cycle:closeCycle") && reviewCycleDetails?.status !== "Completed") {
			options.push({
				key: "close-review-cycle",
				label: "Close Cycle",
				right: <Icon icon={faCircleCheck} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Close Cycle",
						description: "Are you sure you want to close this review cycle?",
						confirmButtonText: "Close Cycle",
						action: () => closeReviewCycle({ reviewCycleId }),
					});
				},
			});
		}

		const canDelete =
			(reviewCycleDetails?.startDate && reviewCycleDetails?.startDate > new Date()) ||
			reviewCycleDetails?.reviewCount === 0;
		if (permissions?.includes("review_cycle:deleteCycle") && canDelete) {
			options.push({
				key: "delete-review-cycle",
				label: "Delete Cycle",
				right: <Icon icon={faTrashCan} size={16} color="neutral-7" />,
				onClick: () => {
					setModal({
						title: "Delete Cycle",
						description: "Are you sure you want to delete this review cycle?",
						confirmButtonText: "Delete Cycle",
						action: () => deleteReviewCycle({ reviewCycleId }),
					});
				},
			});
		}

		return options;
	}, [reviewCycleDetails, permissions, hasFormsAwaitingApproval, automatedReviewCyclesEnabled]);

	const options = isOptionsRefreshEnabled ? refreshedOptions : legacyOptions;

	return (
		<>
			<DropDownMenu
				className={isOptionsRefreshEnabled ? "w-[223px]" : "w-[180px]"}
				menuClassName={isOptionsRefreshEnabled ? "w-[223px]" : "w-[180px]"}
				bordered
				placement="bottom"
				testid="review-cycle-options"
				options={options}
			>
				<div className="flex w-full items-center justify-between gap-[4px]">
					<Text size="paragraph-3" weight="regular" color="neutral-9">
						Options
					</Text>
					<Icon icon={faGear} size={16} color="neutral-7" />
				</div>
			</DropDownMenu>
			<EditAutomationRulesModal
				reviewCycleId={reviewCycleId}
				initialQuery={autoflowUserQuery}
				isOpen={isEditAutomationRulesOpen}
				onClose={() => setIsEditAutomationRulesOpen(false)}
			/>
			<DownloadCsvModal
				isOpen={isDownloadCsvModalOpen}
				onClose={() => setIsDownloadCsvModalOpen(false)}
				onDownload={handleDownloadCsvSelection}
			/>
			<Modal
				isOpen={!!modal}
				content={
					<div className="flex flex-col text-center">
						<h2 className="text-heading-3 font-bold">{modal?.title}</h2>
						<p className="text-paragraph-3">{modal?.description}</p>
					</div>
				}
				handleClose={() => setModal(null)}
				buttons={
					<div className="flex gap-2">
						<ButtonStandard color="primary" onClick={() => setModal(null)}>
							Cancel
						</ButtonStandard>
						<ButtonLoading loading={isClosing} color="green" onClick={() => modal?.action()}>
							{modal?.confirmButtonText}
						</ButtonLoading>
					</div>
				}
			/>
			<EditCycleDetailsModal
				reviewCycleId={reviewCycleId}
				isOpen={editCycleDetailsOpen}
				onClose={() => setEditCycleDetailsOpen(false)}
				onSaved={fullRefresh}
			/>
		</>
	);
};

export default DetailsOptionsDropdown;
